import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Live, in-page preview of the embeddable review widget.
 *
 * This is a faithful React mock of the real launcher
 * (`apps/promoter/public/widget/v1.js`) — same gradient, star, layered shadow
 * and per-variant layout — so the vendor sees how their settings will look
 * without embedding the script. Keep the visual details in sync with v1.js.
 */
interface WidgetPreviewProps {
  variant: string;
  color: string;
  buttonText: string;
  position: string;
  heading?: string;
}

// Mirror of the embed script's shade()/gradient.
function shade(hex: string, amt: number): string {
  let c = hex.replace('#', '');
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  if (!/^[0-9a-fA-F]{6}$/.test(c)) return hex;
  const n = parseInt(c, 16);
  const ch = (x: number) => Math.max(0, Math.min(255, x + amt));
  return `rgb(${ch((n >> 16) & 255)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

const SHADOW = '0 6px 16px -3px rgba(17,24,39,0.22),0 2px 6px -2px rgba(17,24,39,0.12)';

const gradientFor = (color: string) =>
  `linear-gradient(135deg, ${shade(color, 22)} 0%, ${color} 55%, ${shade(color, -26)} 100%)`;

const Star: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2l2.9 6.26 6.86.6-5.18 4.53 1.55 6.71L12 17.1 5.87 20.6l1.55-6.71L2.24 9.36l6.86-.6z" />
  </svg>
);

// Gradient pill (star + label), shared by floating / inline / banner / card.
const Pill: React.FC<{ color: string; text: string; full?: boolean }> = ({ color, text, full }) => (
  <span
    style={{
      display: full ? 'flex' : 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
      width: full ? '100%' : undefined,
      color: '#fff',
      fontWeight: 600,
      fontSize: 13,
      lineHeight: 1,
      borderRadius: 9999,
      padding: '10px 18px',
      background: gradientFor(color),
      boxShadow: SHADOW,
      whiteSpace: 'nowrap',
    }}
  >
    <Star />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
  </span>
);

// Corner placement inside the preview frame (top inset clears the fake chrome).
function corner(position: string): React.CSSProperties {
  const s: React.CSSProperties = { position: 'absolute' };
  if (position.includes('top')) s.top = 46;
  else s.bottom = 16;
  if (position.includes('left')) s.left = 16;
  else s.right = 16;
  return s;
}

const POSITIONED = ['floating', 'bubble', 'card'];

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  variant,
  color,
  buttonText,
  position,
  heading,
}) => {
  const { t } = useTranslation();
  const pos = POSITIONED.includes(variant) ? position : 'bottom-right';

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">
          {t('widget.preview', 'Live preview')}
        </span>
        <span className="text-[11px] text-gray-400 capitalize">{variant}</span>
      </div>

      {/* Faux browser showing the vendor's own page with the widget on it. */}
      <div className="relative h-[340px] overflow-hidden bg-gradient-to-br from-slate-50 to-indigo-50">
        {/* browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-white/70 border-b border-gray-100">
          <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-300" />
          <div className="ml-2 h-4 flex-1 max-w-[180px] rounded bg-gray-200/70" />
        </div>

        {/* page content skeleton */}
        <div className="p-4 space-y-2.5">
          <div className="h-3 w-1/2 rounded bg-gray-200/80" />
          <div className="h-2.5 w-3/4 rounded bg-gray-200/60" />
          <div className="h-2.5 w-2/3 rounded bg-gray-200/60" />

          {variant === 'inline' && (
            <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white/60 p-4">
              <div className="mb-2 text-[11px] font-medium text-gray-400">#clareefai-widget</div>
              <Pill color={color} text={buttonText} />
            </div>
          )}
        </div>

        {/* floating pill */}
        {variant === 'floating' && (
          <div style={corner(pos)}>
            <Pill color={color} text={buttonText} />
          </div>
        )}

        {/* bubble FAB */}
        {variant === 'bubble' && (
          <div style={corner(pos)}>
            <span
              style={{
                width: 52,
                height: 52,
                borderRadius: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: gradientFor(color),
                boxShadow: SHADOW,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
          </div>
        )}

        {/* card */}
        {variant === 'card' && (
          <div style={corner(pos)}>
            <div
              style={{
                boxSizing: 'border-box',
                width: 220,
                background: '#fff',
                borderRadius: 18,
                boxShadow: SHADOW,
                border: '1px solid rgba(17,24,39,0.06)',
                padding: 20,
                textAlign: 'center',
              }}
            >
              <div style={{ color: '#f59e0b', marginBottom: 10, lineHeight: 0 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} />
                ))}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: 14,
                }}
              >
                {heading || t('widget.previewHeading', 'Enjoying our product?')}
              </div>
              <Pill color={color} text={buttonText} full />
            </div>
          </div>
        )}

        {/* banner */}
        {variant === 'banner' && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              padding: 12,
              background: 'rgba(255,255,255,0.82)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderTop: '1px solid rgba(17,24,39,0.08)',
              boxShadow: '0 -2px 12px rgba(17,24,39,0.06)',
            }}
          >
            <Pill color={color} text={buttonText} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetPreview;
